<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/order/PosOrderViewItems.vue.
  // Admin's IOrderItem already embeds item + options metadata, so there is no
  // async catalog fetching; options text reuses the existing
  // orderItemOptionsToText util (returns HTML, like the old view modal).
  import { computed, getCurrentInstance, inject, Ref } from 'vue';
  import { IOrder } from '@/interfaces/IOrder';
  import { IOrderItem } from '@/interfaces/IOrderItem';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { stringDateToHHMM } from '@/utils/date.utils';
  import { orderItemOptionsToText } from '@/utils/orders.utils';
  import StoreMenuItemPicture from '@/components/shared/StoreMenuItemPicture.vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';
  import PosV2PrintIndicators from './PosV2PrintIndicators.vue';

  const order = inject<Ref<IOrder>>('order')!;

  const money = usePosMoney();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  interface IItemNote {
    text: string;
    icon: string;
    color: string;
  }

  interface IItemLineView {
    orderItem: IOrderItem;
    key: string;
    name: string;
    optionsHtml: string;
    note: IItemNote | null;
    isCanceled: boolean;
  }

  function getOptionsHtml(orderItem: IOrderItem): string {
    if (!orderItem.options?.length) return '';
    try {
      return orderItemOptionsToText({ orderItem });
    } catch {
      // Defensive: incomplete option metadata on stale socket payloads.
      return '';
    }
  }

  function getItemNote(orderItem: IOrderItem): IItemNote | null {
    if (orderItem.canceledAt) {
      return {
        text: i18nT('posV2.itemRemovedAt', {
          time: stringDateToHHMM(orderItem.canceledAt),
        }),
        icon: 'delete',
        color: 'tw-text-red-600',
      };
    }
    if (orderItem.addedAt) {
      return {
        text: i18nT('posV2.itemAddedAt', {
          time: stringDateToHHMM(orderItem.addedAt),
        }),
        icon: 'plus',
        color: 'tw-text-emerald-600',
      };
    }
    if (orderItem.modifiedAt) {
      return {
        text: i18nT('posV2.itemModifiedAt', {
          time: stringDateToHHMM(orderItem.modifiedAt),
        }),
        icon: 'pencil',
        color: 'tw-text-amber-500',
      };
    }
    return null;
  }

  const itemLines = computed<IItemLineView[]>(() => {
    const items = [...(order.value.orderItems ?? [])].sort(
      (a, b) => (a.index ?? 0) - (b.index ?? 0)
    );

    return items.map((orderItem) => ({
      orderItem,
      key: `${orderItem.itemId}|${orderItem.index ?? 0}`,
      name:
        orderItem.item?.name ||
        i18nT('posV2.productFallback', { id: orderItem.itemId }),
      optionsHtml: getOptionsHtml(orderItem),
      note: getItemNote(orderItem),
      isCanceled: !!orderItem.canceledAt,
    }));
  });

  const activeCount = computed(
    () => itemLines.value.filter((l) => !l.isCanceled).length
  );
</script>

<template>
  <div class="pos-v2-view-items">
    <div class="items-header">
      <h3 class="items-title">{{ $t('posV2.products') }}</h3>
      <PosUiBadge
        v-if="activeCount > 0"
        :label="activeCount"
        color="primary"
        variant="soft"
        size="sm"
      />
    </div>

    <PosUiScrollable
      class="items-body-container"
      orientation="vertical"
      variant="pseudo"
      theme="page"
      :offset-top="10"
      :offset-bottom="10"
      size="md"
      scroll-class="items-body"
    >
      <PosUiEmptyState
        v-if="itemLines.length === 0"
        icon="package-variant"
        :title="$t('posV2.noProductsInOrder')"
        class="items-empty"
      />

      <div v-else class="items-list">
        <div
          v-for="line in itemLines"
          :key="line.key"
          class="item-line"
          :class="{ 'is-canceled': line.isCanceled }"
        >
          <div class="line-image">
            <StoreMenuItemPicture :item="line.orderItem.item" size="lg" />
          </div>

          <div class="line-info">
            <div class="line-name">{{ line.name }}</div>
            <!-- eslint-disable-next-line vue/no-v-html — trusted util output -->
            <div
              v-if="line.optionsHtml"
              class="line-options"
              v-html="line.optionsHtml"
            ></div>
            <div v-if="line.orderItem.comment" class="line-comment">
              <PosUiIcon name="comment-outline" class="comment-icon" />
              <span class="comment-text">{{ line.orderItem.comment }}</span>
            </div>
            <div v-if="line.note" class="line-note">
              <PosUiIcon
                :name="line.note.icon"
                class="note-icon"
                :class="line.note.color"
              />
              <span>{{ line.note.text }}</span>
            </div>
          </div>

          <div class="line-price">
            <div class="line-qty-row">
              <PosV2PrintIndicators
                :quantity="line.orderItem.amount"
                :print-c="line.orderItem.printC"
                :print-e="line.orderItem.printE"
                :is-canceled="line.isCanceled"
              />
              <div class="line-quantity">x{{ line.orderItem.amount }}</div>
            </div>
            <div class="unit-price">
              {{ money.formatComputedMoney(line.orderItem.price) }}
            </div>
            <div class="total-price">
              {{ money.formatComputedMoney(line.orderItem.total) }}
            </div>
          </div>
        </div>
      </div>
    </PosUiScrollable>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-view-items {
    @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white;

    .items-header {
      @apply tw-flex tw-flex-none tw-items-center tw-justify-between tw-border-b tw-border-neutral-100 tw-px-4 tw-py-3;

      .items-title {
        @apply tw-mb-0 tw-text-lg tw-font-semibold tw-text-neutral-900;
      }
    }

    .items-body-container {
      @apply tw-relative tw-min-h-0 tw-flex-1;
    }

    :deep(.items-body) {
      @apply tw-h-full tw-overflow-y-auto;
    }

    .items-empty {
      @apply tw-py-8;
    }

    .items-list {
      @apply tw-flex tw-min-h-full tw-flex-col tw-gap-1.5 tw-p-2;
    }

    .item-line {
      @apply tw-flex tw-min-h-[72px] tw-gap-2.5 tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-p-2.5;

      &.is-canceled {
        @apply tw-border-red-600/30 tw-bg-red-600/5 tw-opacity-60;

        .line-name,
        .line-options,
        .line-price {
          @apply tw-text-neutral-500 tw-line-through tw-decoration-red-600 tw-decoration-2;
        }
      }

      .line-image {
        @apply tw-h-16 tw-w-16 tw-flex-shrink-0 tw-overflow-hidden tw-rounded-lg;
      }

      .line-info {
        @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5;

        .line-name {
          @apply tw-text-sm tw-font-semibold tw-text-neutral-900;
        }

        .line-options {
          @apply tw-text-xs tw-text-neutral-500;
        }

        .line-comment {
          @apply tw-mt-0.5 tw-flex tw-items-start tw-gap-1 tw-text-xs tw-text-sky-700;

          .comment-icon {
            @apply tw-mt-0.5 tw-flex-shrink-0 tw-text-sm;
          }

          .comment-text {
            @apply tw-line-clamp-2;
          }
        }

        .line-note {
          @apply tw-mt-0.5 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-neutral-500;

          .note-icon {
            @apply tw-text-sm;
          }
        }
      }

      .line-price {
        @apply tw-flex tw-flex-shrink-0 tw-flex-col tw-items-end tw-gap-0.5;

        .line-qty-row {
          @apply tw-flex tw-items-center tw-justify-end tw-gap-1.5;

          .line-quantity {
            @apply tw-text-xs tw-font-medium tw-text-neutral-500;
          }
        }

        .unit-price {
          @apply tw-text-[10px] tw-leading-tight tw-text-neutral-500;
        }

        .total-price {
          @apply tw-text-sm tw-font-semibold tw-tabular-nums tw-text-neutral-900;
        }
      }
    }
  }
</style>
